import * as React from "react";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard/";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import moment from "moment";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import ExportService from "@app/exports/exportService";

export interface UsageReportProps {
  clientId: number;
  handleUsageReportToast: (status: AccessReportStatusEnum) => void;
  username: string;
}

export const UsageReport = (props: UsageReportProps): JSX.Element => {
  const [startDate, setStartDate] = React.useState<string>("");
  const [startDateValid, setStartDateValid] = React.useState<boolean>(false);
  const [endDate, setEndDate] = React.useState<string>("");
  const [endDateValid, setEndDateValid] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const twoYearsAgoDate = formatUnconvertedDateCustom(moment().subtract(2, "years"), "YYYY-MM-DD");
  const todaysDate = formatUnconvertedDateCustom(new Date(), "YYYY-MM-DD");

  React.useEffect(() => {
    if (!!startDate &&
      (!endDate || startDate <= endDate) &&
      startDate >= twoYearsAgoDate &&
      startDate <= todaysDate
    ) {
      setStartDateValid(true);
    } else {
      setStartDateValid(false);
    }
  }, [startDate]);

  React.useEffect(() => {
    if (!!endDate &&
      (!startDate || endDate >= startDate) &&
      endDate >= twoYearsAgoDate &&
      endDate <= todaysDate
    ) {
      setEndDateValid(true);
    } else {
      setEndDateValid(false);
    }
  }, [endDate]);

  const handleGenerateReportClick = async (): Promise<void> => {
    try {
      setLoading(true);
      props.handleUsageReportToast(AccessReportStatusEnum.Running);

      const exportService: ExportService = new ExportService();
      const exportBlob: Blob = await exportService.postExportAccessUsers({
        clientId: props.clientId,
        endDate: new Date(endDate + " 23:59:59 UTC+0"),
        productTypeName: "",
        startDate: new Date(startDate),
      });

      if (!!exportBlob) {
        const link: HTMLAnchorElement = document.createElement("a");
        link.href = window.URL.createObjectURL(exportBlob);
        link.setAttribute("download", "file.xlsx");
        document.body.appendChild(link);
        link.download = `UsageReport${formatUnconvertedDateCustom(startDate, "MM-DD-YYYY")}-${formatUnconvertedDateCustom(endDate, "MM-DD-YYYY")}`;
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {

    } finally {
      setLoading(false);
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <QESDateInput
            className="p-1"
            containerClassName="mb-3"
            errorText="Please enter a valid Start Date"
            isValid={startDateValid}
            label="Start Date"
            max={endDate || todaysDate}
            min={twoYearsAgoDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
            required={true}
            testId="access-reports-dashboard__usage-report--start-date"
            value={startDate}
          />

          <QESDateInput
            className="p-1"
            containerClassName="mb-3"
            errorText="Please enter a valid End Date"
            isValid={endDateValid}
            label="End Date"
            max={todaysDate}
            min={startDate || twoYearsAgoDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
            required={true}
            testId="access-reports-dashboard__usage-report--end-date"
            value={endDate}
          />
        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            className="ms-3"
            disabled={!startDateValid || !endDateValid || loading}
            onClick={() => handleGenerateReportClick()}
            qesTestId="access-reports-dashboard__usage-report--generate-report"
            qesType="primary"
          >
            Generate Report
          </QESButton>
        }
      />
    </React.Fragment>
  );
};
